import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Play, ArrowLeft, Video, 
  ChevronRight, Folder, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoLessons() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const categoriesRes = await axios.get(`${API}/video-courses/categories`, { withCredentials: true });
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching video data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWatchVideo = (video) => {
    setPlayingVideo(video);
  };

  const getVimeoEmbedUrl = (url) => {
    // Vimeo URL'sini embed formatına çevir
    // https://vimeo.com/123456789 -> https://player.vimeo.com/video/123456789
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (match) {
      return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
    }
    // Eğer zaten player URL ise
    if (url.includes('player.vimeo.com')) {
      return url + (url.includes('?') ? '&autoplay=1' : '?autoplay=1');
    }
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 to-blue-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">📹</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Video izleme modalı
  if (playingVideo) {
    return (
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <div className="bg-slate-800 border-b border-slate-700 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button
              onClick={() => setPlayingVideo(null)}
              className="flex items-center gap-2 text-white hover:text-blue-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Geri Dön</span>
            </button>
            <h1 className="text-white font-semibold truncate max-w-md">
              {playingVideo.title}
            </h1>
            <div className="w-20"></div>
          </div>
        </div>

        {/* Video Player */}
        <div className="max-w-6xl mx-auto p-4">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            {/* X Kapatma Butonu */}
            <button
              onClick={() => setPlayingVideo(null)}
              className="absolute -top-2 -right-2 z-10 w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg transition-all hover:scale-110"
              title="Kapat"
            >
              <X className="w-6 h-6" />
            </button>
            <iframe
              src={getVimeoEmbedUrl(playingVideo.vimeo_url)}
              className="absolute top-0 left-0 w-full h-full rounded-xl"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={playingVideo.title}
            />
          </div>
          
          {/* Video Bilgisi */}
          <div className="mt-4 p-4 bg-slate-800 rounded-xl">
            <h2 className="text-xl font-bold text-white">{playingVideo.title}</h2>
          </div>
        </div>
      </div>
    );
  }

  // Kategori detay görünümü
  if (selectedCategory) {
    const category = categories.find(c => c.id === selectedCategory);

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 pt-6 pb-8">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Geri</span>
            </button>
            <h1 className="text-2xl font-bold text-white">{category?.title}</h1>
            {category?.description && (
              <p className="text-white/80 mt-2">{category.description}</p>
            )}
          </div>
        </div>

        {/* Video Listesi */}
        <div className="max-w-4xl mx-auto px-4 -mt-4">
          <div className="space-y-3">
            {category?.videos?.map((video, index) => {
              return (
                <Card 
                  key={video.id}
                  className="cursor-pointer hover:shadow-lg transition-all border-0 bg-white"
                  onClick={() => handleWatchVideo(video)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Sıra Numarası */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-100 text-purple-600">
                        <span className="font-bold">{index + 1}</span>
                      </div>
                      
                      {/* Video Bilgisi */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {video.title}
                        </h3>
                      </div>
                      
                      {/* Play Button */}
                      <Button 
                        size="sm"
                        className="bg-purple-500 hover:bg-purple-600"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        İzle
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            
            {(!category?.videos || category.videos.length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Bu kategoride henüz video yok</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Padding */}
        <div className="h-8" />
      </div>
    );
  }

  // Ana görünüm - Kategoriler
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-4 pt-6 pb-12 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Ana Menü</span>
          </button>
          
          <div className="text-center text-white">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Video className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold">Videolu Dersler</h1>
            <p className="text-white/80 mt-2">Eğitim videolarını izleyerek öğrenin</p>
          </div>
        </div>
      </div>

      {/* Kategoriler */}
      <div className="max-w-4xl mx-auto px-4 -mt-6">
        <div className="space-y-4">
          {categories.map((category) => {
            const videoCount = category.videos?.length || 0;
            
            return (
              <Card 
                key={category.id}
                className="cursor-pointer hover:shadow-lg transition-all border-0 shadow-md overflow-hidden"
                onClick={() => setSelectedCategory(category.id)}
              >
                <CardContent className="p-0">
                  <div className="flex items-center">
                    {/* Icon Section */}
                    <div className="w-24 h-28 bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                      <Folder className="w-10 h-10 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{category.title}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {videoCount} video
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {categories.length === 0 && (
            <div className="text-center py-12">
              <Video className="w-20 h-20 mx-auto mb-4 text-gray-300" />
              <h3 className="text-gray-500 font-medium">Henüz video kategorisi yok</h3>
              <p className="text-gray-400 text-sm mt-1">Yakında eklenecek</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center pb-8">
        <p className="text-xs text-gray-400">
          © 2025 Kariyer Online. Tüm Hakları Saklıdır.
        </p>
      </div>
    </div>
  );
}
